import React, { useState, useEffect, useRef } from 'react';
import { ethers } from 'ethers';
import Card from '../common/Card';
import MiniLoader from '../common/MiniLoader';
import { useContracts } from '../../hooks/useContract';
import { useWallet } from '../../context/WalletContext';
import { useNotify } from '../../context/NotificationContext';
import { parseError } from '../../utils/errors';
import { formatUsdt, parseUsdt } from '../../utils/format';
import { CONTRACTS } from '../../config/contracts';
import { shortenAddress } from '../../utils/wallet';

// Emergency USDT withdrawal from the storage contract.
// onlyOwnerOrAdmin — sends USDT to msg.sender (whoever is calling).
const EmergencyWithdraw = () => {
  const { storage, usdt } = useContracts();
  const { account } = useWallet();
  const notify = useNotify();

  const [contractBal, setContractBal] = useState(ethers.BigNumber.from(0));
  const [amount, setAmount] = useState('');
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const runIdRef = useRef(0);

  const load = async () => {
    const myRun = ++runIdRef.current;
    setLoading(true);
    try {
      const bal = await usdt.balanceOf(CONTRACTS.GlobalStorage);
      if (myRun !== runIdRef.current) return;
      setContractBal(bal);
    } catch (_) { /* ignore */ }
    finally {
      if (myRun === runIdRef.current) setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, []);

  const setMax = () => {
    if (!contractBal.isZero()) {
      setAmount(ethers.utils.formatUnits(contractBal, 18));
    }
  };

  const withdraw = async () => {
    if (!amount) { notify.error('Enter amount to withdraw'); return; }
    const amt = parseUsdt(amount);
    if (amt.isZero()) { notify.error('Amount must be greater than 0'); return; }
    if (amt.gt(contractBal)) { notify.error('Amount exceeds contract balance'); return; }

    setBusy(true);
    const l = notify.loading('Executing emergency withdraw...');
    try {
      const tx = await storage.emergencyWithdraw(amt);
      notify.dismiss(l);
      const m = notify.loading('Confirming...');
      await tx.wait();
      notify.dismiss(m);
      notify.success(`Withdrew ${amount} USDT to your wallet`);
      setAmount('');
      setConfirmOpen(false);
      await load();
    } catch (err) {
      notify.dismiss(l);
      notify.error(parseError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card title="Emergency Withdraw" subtitle="Withdraw USDT directly from the storage contract" glow>
      <div className="ew-warning">
        <div className="ew-warning-icon">⚠</div>
        <div>
          <div style={{ fontWeight: 700, marginBottom: 4 }}>Use with extreme caution</div>
          <div style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>
            Emergency withdraw pulls USDT from the storage contract directly to your connected wallet.
            This bypasses normal payout logic and can leave the contract short of funds for user claims,
            royalty payouts, and package distributions.
          </div>
        </div>
      </div>

      <div className="stat mb-md" style={{ marginTop: 16 }}>
        <span className="stat-label">Storage USDT Balance</span>
        <span className="stat-value-lg">
          {loading ? '...' : formatUsdt(contractBal)}<span className="stat-unit">USDT</span>
        </span>
      </div>

      <div className="input-group">
        <label className="input-label">Withdraw Amount</label>
        <div className="flex gap-sm">
          <input
            className="input"
            type="number"
            placeholder="0.00"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            step="0.0001"
            min="0"
          />
          <button className="btn btn-secondary btn-sm" onClick={setMax} type="button" disabled={contractBal.isZero()}>
            Max
          </button>
        </div>
      </div>

      <div className="ew-destination">
        <div className="ew-destination-label">Funds go to</div>
        <div className="ew-destination-addr font-mono">
          {account ? shortenAddress(account, 6) : '-'}
        </div>
      </div>

      {!confirmOpen ? (
        <button
          className="btn btn-error btn-full mt-md"
          onClick={() => setConfirmOpen(true)}
          disabled={busy || !amount || contractBal.isZero()}
        >
          Prepare Emergency Withdraw
        </button>
      ) : (
        <div className="ew-confirm">
          <div className="ew-confirm-title">Confirm: Withdraw <strong>{amount} USDT</strong>?</div>
          <div className="ew-confirm-desc">
            This will immediately transfer {amount} USDT from the storage contract to
            <span className="font-mono" style={{ color: 'var(--aurora-cyan)', marginLeft: 4 }}>
              {shortenAddress(account, 6)}
            </span>.
          </div>
          <div className="flex gap-sm mt-md">
            <button className="btn btn-secondary flex-1" onClick={() => setConfirmOpen(false)} disabled={busy}>
              Cancel
            </button>
            <button className="btn btn-error flex-1" onClick={withdraw} disabled={busy}>
              {busy ? <><MiniLoader /> Withdrawing</> : 'Yes, Withdraw'}
            </button>
          </div>
        </div>
      )}
    </Card>
  );
};

export default EmergencyWithdraw;
